import React, { useState, useRef } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  LinearProgress, 
  IconButton,
  Alert
} from "@mui/material";
import { 
  CloudUpload, 
  VideoFile, 
  Delete,
  CheckCircle 
} from "@mui/icons-material";
import DemoPromoComponent from "./DemoPromoComponent";

const DemoUploadVideosStep = ({ onComplete }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    setError("");
    
    // Demo restriction: Only allow 1 video
    if (uploadedFiles.length > 0) {
      setError("For the demo, you can only upload 1 video");
      return;
    }
    
    // Demo restriction: Only process the first file if multiple are selected
    const firstFile = files[0];
    if (!firstFile) return;
    
    if (!firstFile.type.startsWith('video/')) {
      setError("Please upload only video files");
      return;
    }
    
    if (firstFile.size > 500 * 1024 * 1024) { // 500MB limit
      setError("Please upload videos smaller than 500MB");
      return;
    }

    const fileId = Date.now() + Math.random();
    
    // Create video element to get duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = function() {
      const durationMinutes = video.duration / 60;
      
      // Demo restriction: Check 10-minute limit
      if (durationMinutes > 10) {
        setError("For the demo, please upload videos under 10 minutes");
        URL.revokeObjectURL(video.src);
        return;
      }
      
      const fileWithId = {
        id: fileId,
        file: firstFile,
        name: firstFile.name,
        size: firstFile.size,
        type: firstFile.type,
        duration: video.duration, // Duration in seconds
        uploaded: false
      };
      
      setUploadedFiles(prev => [...prev, fileWithId]);
      simulateUpload(fileId, firstFile);
      
      // Clean up
      URL.revokeObjectURL(video.src);
    };
    
    video.onerror = function() {
      setError("Unable to read video file. Please try a different video.");
      URL.revokeObjectURL(video.src);
    };
    
    video.src = URL.createObjectURL(firstFile);
  };

  const simulateUpload = (fileId, file) => {
    // Simulate upload progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadedFiles(prev => 
          prev.map(f => f.id === fileId ? { ...f, uploaded: true } : f)
        );
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      } else {
        setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
      }
    }, 200);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleContinue = () => {
    if (uploadedFiles.length === 0) {
      setError("Please upload at least one video before continuing");
      return;
    }
    
    const allUploaded = uploadedFiles.every(file => file.uploaded);
    if (!allUploaded) {
      setError("Please wait for all videos to finish uploading");
      return;
    }

    // Pass the uploaded files to the parent component
    onComplete(uploadedFiles);
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        backgroundColor: "#121212",
        overflow: "hidden",
      }}
    >
      {/* Left Side - Promotional Component */}
      <Box sx={{ 
        width: "50%",
        height: "100vh",
        overflow: "hidden"
      }}>
        <DemoPromoComponent />
      </Box>

      {/* Right Side - Upload Functionality */}
      <Box
        sx={{
          width: "50%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 4,
          py: 4,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "500px",
            mx: "auto",
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              mb: 2,
              letterSpacing: "-0.02em",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Upload Your Videos
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "#94a3b8",
              fontWeight: 400,
              mb: 3,
              lineHeight: 1.6,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Upload videos of your application workflows to see the magic happen.
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                backgroundColor: "rgba(211, 47, 47, 0.1)",
                color: "#ff6b6b",
                "& .MuiAlert-icon": {
                  color: "#ff6b6b"
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              border: `2px dashed ${dragActive ? "rgba(255, 255, 255, 0.5)" : "rgba(255, 255, 255, 0.2)"}`,
              borderRadius: 3,
              p: 4,
              mb: 3,
              textAlign: "center",
              backgroundColor: dragActive ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.02)",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.3)",
                backgroundColor: "rgba(255, 255, 255, 0.04)",
              },
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 40, color: "#94a3b8", mb: 1.5 }} />
            <Typography
              sx={{
                color: "#94a3b8",
                fontSize: "1.1rem",
                mb: 1,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              {uploadedFiles.length === 0 ? "Drag and drop a video here or click to browse" : "1 video uploaded"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontSize: "0.9rem",
                fontStyle: "italic",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              (1 video only, under 10 minutes, Max 500MB)
            </Typography>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
          </Box>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mb: 3, maxHeight: "200px", overflowY: "auto" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  mb: 1.5,
                  textAlign: "left",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                Uploaded Video
              </Typography>
              {uploadedFiles.map((file) => (
                <Box
                  key={file.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1.5,
                  }}
                >
                  <VideoFile sx={{ color: "#94a3b8", mr: 2 }} />
                  <Box sx={{ flexGrow: 1, textAlign: "left" }}>
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 500,
                        mb: 0.5,
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                    {!file.uploaded && uploadProgress[file.id] !== undefined && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress[file.id]}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#4caf50",
                          },
                        }}
                      />
                    )}
                  </Box>
                  {file.uploaded && (
                    <CheckCircle sx={{ color: "#4caf50", mr: 1 }} />
                  )}
                  <IconButton
                    onClick={() => removeFile(file.id)}
                    sx={{ color: "#94a3b8" }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Button
            fullWidth
            onClick={handleContinue}
            disabled={uploadedFiles.length === 0 || !uploadedFiles.every(file => file.uploaded)}
            sx={{
              maxWidth: "320px",
              height: 48,
              borderRadius: 2,
              mb: 3,
              background: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                ? "#ffffff" 
                : "rgba(255, 255, 255, 0.3)",
              color: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                ? "#000000" 
                : "rgba(255, 255, 255, 0.7)",
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              boxShadow: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                ? "0 4px 16px rgba(255, 255, 255, 0.2)" 
                : "none",
              "&:hover": {
                transform: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                  ? "translateY(-2px)" 
                  : "none",
                boxShadow: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                  ? "0 8px 24px rgba(255, 255, 255, 0.25)" 
                  : "none",
                background: uploadedFiles.length > 0 && uploadedFiles.every(file => file.uploaded) 
                  ? "#f8fafc" 
                  : "rgba(255, 255, 255, 0.3)",
              },
              "&:disabled": {
                color: "rgba(255, 255, 255, 0.5)",
              }
            }}
          >
            Continue {uploadedFiles.length > 0 && `(${uploadedFiles.filter(f => f.uploaded).length}/${uploadedFiles.length})`}
          </Button>

          {uploadedFiles.length === 0 && (
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                borderRadius: 3,
                p: 3,
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  mb: 1.5,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                💡 Demo Tips
              </Typography>
              <Box
                component="ul"
                sx={{
                  color: "#94a3b8",
                  pl: 2,
                  m: 0,
                  "& > li": {
                    mb: 0.5,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  },
                }}
              >
                <li>Upload 1 video under 10 minutes to see the demo</li>
                <li>Show a complete workflow from start to finish</li>
                <li>Experience how AI converts videos to tests</li>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DemoUploadVideosStep; 